import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Store, Phone, Loader2, ArrowRight, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { sendOtp as msg91SendOtp, verifyOtp as msg91VerifyOtp, resendOtp as msg91ResendOtp, clearOtpSession } from '../lib/msg91';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mock restaurant data for quick login
const MOCK_RESTAURANTS = [
  { name: 'Green Leaf', phone: '5555555555' },
  { name: 'Spice Garden', phone: '1111111111' },
  { name: 'Biryani House', phone: '2222222222' },
  { name: 'Pizza Palace', phone: '3333333333' },
  { name: 'Dragon Wok', phone: '4444444444' },
  { name: 'Royal Kitchen', phone: '6666666666' },
];

export const RestaurantLogin = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [restaurantData, setRestaurantData] = useState(null);
  const [otpMode, setOtpMode] = useState('demo'); // 'demo', 'twilio', or 'msg91'
  const [demoOtp, setDemoOtp] = useState('');

  useEffect(() => {
    // Check if already logged in
    const restaurant = localStorage.getItem('fudco_restaurant_session');
    if (restaurant) {
      navigate('/restaurant-dashboard');
    }
  }, [navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Clear MSG91 session when component unmounts or phone changes
  useEffect(() => {
    return () => clearOtpSession();
  }, [phone]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    
    if (phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    try {
      // First check if restaurant exists
      const checkRes = await axios.get(`${API}/restaurant/check/${phone}`);
      
      if (!checkRes.data.exists) {
        toast.error('No restaurant found with this phone number. Please register first.');
        setLoading(false);
        return;
      }

      setRestaurantData(checkRes.data.restaurant);

      // Check with backend what OTP mode to use
      const otpRes = await axios.post(`${API}/auth/send-otp`, { phone });
      const mode = otpRes.data.mode || 'demo';
      setOtpMode(mode);
      
      // For MSG91 mode, use the frontend SDK to send OTP
      if (mode === 'msg91' && otpRes.data.use_sdk) {
        try {
          await msg91SendOtp(phone);
          toast.success('OTP sent successfully!');
        } catch (sdkError) {
          console.error('MSG91 SDK error:', sdkError);
          toast.error('Failed to send OTP. Please try again.');
          setLoading(false);
          return;
        }
      } else {
        toast.success('OTP sent successfully!');
        
        // Show demo OTP for testing modes
        if (otpRes.data.demo_otp) {
          setDemoOtp(otpRes.data.demo_otp);
          toast.info(`Demo OTP: ${otpRes.data.demo_otp}`, {
            duration: 30000,
            description: 'Use this OTP for testing'
          });
        }
      }

      setStep('otp');
      setCountdown(60);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    
    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      // For MSG91 mode, verify via SDK first, then backend
      if (otpMode === 'msg91') {
        try {
          // Verify OTP with MSG91 SDK and get access token
          const { accessToken } = await msg91VerifyOtp(otp);
          
          // Verify access token with backend
          const verifyRes = await axios.post(`${API}/auth/msg91/verify`, {
            access_token: accessToken,
            role: 'restaurant',
            phone: phone
          });
          
          if (!verifyRes.data.success) {
            throw new Error('Backend verification failed');
          }
          
          toast.success(`Welcome back, ${restaurantData?.restaurant_name}!`);
          
          // Store session
          localStorage.setItem('fudco_restaurant_session', JSON.stringify({
            id: restaurantData?.id,
            name: restaurantData?.restaurant_name,
            phone: phone,
            loggedInAt: new Date().toISOString()
          }));
          
          // Update last login on server
          await axios.post(`${API}/restaurant/login`, { phone, otp }).catch(() => {});
          
          navigate('/restaurant-dashboard');
          return;
        } catch (sdkError) {
          console.error('MSG91 verification error:', sdkError);
          toast.error('Invalid or expired OTP. Please try again.');
          setLoading(false);
          return;
        }
      }
      
      // For demo/twilio mode, verify via backend
      await axios.post(`${API}/restaurant/login`, {
        phone,
        otp
      });

      toast.success(`Welcome back, ${restaurantData?.restaurant_name}!`);
      
      // Store session
      localStorage.setItem('fudco_restaurant_session', JSON.stringify({
        id: restaurantData?.id,
        name: restaurantData?.restaurant_name,
        phone: phone,
        loggedInAt: new Date().toISOString()
      }));

      // Redirect to dashboard
      navigate('/restaurant-dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;
    
    try {
      if (otpMode === 'msg91') {
        // Use MSG91 SDK to resend
        await msg91ResendOtp(phone, 'sms');
        toast.success('OTP resent successfully!');
      } else {
        // Use backend to resend
        const otpRes = await axios.post(`${API}/auth/send-otp`, { phone });
        toast.success('OTP resent successfully!');
        
        if (otpRes.data.demo_otp) {
          setDemoOtp(otpRes.data.demo_otp);
          toast.info(`Demo OTP: ${otpRes.data.demo_otp}`, {
            duration: 30000,
            description: 'Use this OTP for testing'
          });
        }
      }
      setCountdown(60);
    } catch (error) {
      toast.error('Failed to resend OTP');
    }
  };

  const handleQuickLogin = (mockPhone) => {
    setPhone(mockPhone);
    toast.info(`Phone number set to ${mockPhone}. Click "Send OTP" to continue.`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white pt-20 pb-10 px-4">
      <div className="max-w-md mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#2D8B2E] rounded-2xl mb-4">
            <Store className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Restaurant Login
          </h1>
          <p className="text-gray-600 mt-2">
            Access your Fudco Restaurant Dashboard
          </p>
        </div>

        <Card className="p-6 shadow-xl border-0">
          {step === 'phone' ? (
            <form onSubmit={handleSendOtp} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="phone">Registered Phone Number</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">+91</span>
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => {
                      if (/^\d*$/.test(e.target.value) && e.target.value.length <= 10) {
                        setPhone(e.target.value);
                      }
                    }}
                    placeholder="Enter 10-digit number"
                    className="pl-12 h-12 text-lg"
                    data-testid="restaurant-phone-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={loading || phone.length !== 10}
                className="w-full h-12 bg-[#2D8B2E] hover:bg-[#236c24] text-white rounded-xl font-semibold"
                data-testid="send-otp-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Send OTP
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              {/* Quick Login for Mock Restaurants */}
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-500 mb-3 text-center">Quick Login (Test Accounts)</p>
                <div className="grid grid-cols-2 gap-2">
                  {MOCK_RESTAURANTS.map((restaurant) => (
                    <Button
                      key={restaurant.phone}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleQuickLogin(restaurant.phone)}
                      className="text-xs h-9 border-green-200 hover:bg-green-50"
                      data-testid={`quick-login-${restaurant.phone}`}
                    >
                      {restaurant.name}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-2 text-center">
                  OTP for test accounts: <span className="font-mono font-bold">123456</span>
                </p>
              </div>

              <div className="text-center">
                <p className="text-sm text-gray-500">
                  New restaurant?{' '}
                  <button 
                    type="button"
                    onClick={() => navigate('/restaurant-partner')}
                    className="text-[#2D8B2E] font-semibold hover:underline"
                  >
                    Register here
                  </button>
                </p>
              </div>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-6">
              {/* Restaurant Info */}
              {restaurantData && (
                <div className="p-4 bg-green-50 rounded-xl flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#2D8B2E] rounded-xl flex items-center justify-center">
                    <Store className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{restaurantData.restaurant_name}</p>
                    <p className="text-sm text-gray-500">+91 {phone}</p>
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-600 ml-auto" />
                </div>
              )}

              <div className="text-center">
                <div className="bg-green-100 text-[#2D8B2E] p-4 rounded-full inline-block mb-4">
                  <Phone className="w-8 h-8" />
                </div>
                <p className="text-gray-600 text-sm">
                  Enter the OTP sent to <span className="font-semibold">+91 {phone}</span>
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="otp">Enter OTP</Label>
                <Input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    if (/^\d*$/.test(e.target.value) && e.target.value.length <= 6) {
                      setOtp(e.target.value);
                    }
                  }}
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                  className="h-14 text-center text-2xl tracking-[0.5em] rounded-xl font-mono"
                  data-testid="restaurant-otp-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full h-12 bg-[#2D8B2E] hover:bg-[#236c24] text-white rounded-xl font-semibold"
                data-testid="verify-otp-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Verify & Login'
                )}
              </Button>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    setStep('phone');
                    setOtp('');
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  ← Change Number
                </button>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={countdown > 0}
                  className={`text-sm ${countdown > 0 ? 'text-gray-400' : 'text-[#2D8B2E] hover:underline'}`}
                >
                  {countdown > 0 ? `Resend in ${countdown}s` : 'Resend OTP'}
                </button>
              </div>
            </form>
          )}
        </Card>

        {/* Help Text */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Need help? Contact{' '}
          <a href="mailto:service@fudco.in" className="text-[#2D8B2E] hover:underline">
            service@fudco.in
          </a>
        </p>
      </div>
    </div>
  );
};

export default RestaurantLogin;
